import { useState } from "react";
import { reportTemplates, ReportTemplate, planRevenue, factRevenue, planMargin, factMargin, forecastRevenue } from "@/mocks/rop";

function formatMoney(value: number): string {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(value);
}

interface ReportsModalProps {
  onClose: () => void;
}

export default function ReportsModal({ onClose }: ReportsModalProps) {
  const [selectedReport, setSelectedReport] = useState<ReportTemplate | null>(null);

  return (
    <div className="fixed inset-0 bg-foreground-950/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg border border-[#D5CA8A] w-full max-w-4xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 bg-[#FEF3C7] border-b border-[#D5CA8A] rounded-t-lg">
          <div className="flex items-center gap-2">
            <i className="ri-file-list-3-line text-[#D97706]"></i>
            <span className="text-[12px] font-semibold text-[#2D2D2D]">Отчеты для руководителей ({reportTemplates.length})</span>
          </div>
          <button onClick={onClose} className="w-6 h-6 flex items-center justify-center rounded hover:bg-[#E8DB6C] cursor-pointer">
            <i className="ri-close-line text-[#666666]"></i>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {/* Plan/Fact Summary */}
          <div className="p-4 bg-[#FDF2B5]/50 border border-[#D5CA8A] rounded-lg mb-4">
            <p className="text-[12px] font-semibold text-[#2D2D2D] mb-3">План / факт (июнь 2026)</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-[#666666]">Выручка (план)</span>
                  <span className="text-[#2D2D2D] font-medium">{formatMoney(planRevenue)}</span>
                </div>
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-[#666666]">Выручка (факт)</span>
                  <span className="text-[#2D6A4F] font-medium">{formatMoney(factRevenue)}</span>
                </div>
                <div className="w-full h-2 bg-[#D5CA8A]/30 rounded-full">
                  <div className="h-full bg-[#E8DB6C] rounded-full" style={{ width: `${Math.min((factRevenue / planRevenue) * 100, 100)}%` }}></div>
                </div>
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-[#666666]">Выполнение</span>
                  <span className={factRevenue >= planRevenue ? "text-[#2D6A4F] font-medium" : "text-[#D97706] font-medium"}>
                    {((factRevenue / planRevenue) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-[#666666]">Маржа (план)</span>
                  <span className="text-[#2D2D2D] font-medium">{formatMoney(planMargin)}</span>
                </div>
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-[#666666]">Маржа (факт)</span>
                  <span className="text-[#2D6A4F] font-medium">{formatMoney(factMargin)}</span>
                </div>
                <div className="w-full h-2 bg-[#D5CA8A]/30 rounded-full">
                  <div className="h-full bg-[#2D6A4F] rounded-full" style={{ width: `${Math.min((factMargin / planMargin) * 100, 100)}%` }}></div>
                </div>
                <div className="flex items-center justify-between text-[10px]">
                  <span className="text-[#666666]">Прогноз выручки</span>
                  <span className="text-[#2D6A4F] font-medium">{formatMoney(forecastRevenue)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Report Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {reportTemplates.map((r) => (
              <div
                key={r.name}
                className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                  selectedReport?.name === r.name
                    ? "border-[#D97706] bg-[#FEF3C7]"
                    : "border-[#D5CA8A] hover:bg-[#FDF2B5]/30"
                }`}
                onClick={() => setSelectedReport(selectedReport?.name === r.name ? null : r)}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] font-semibold text-[#2D2D2D]">{r.name}</span>
                  <span className="text-[9px] text-[#666666] bg-[#FDF2B5] px-1.5 py-0.5 rounded">{r.lastGenerated}</span>
                </div>
                <p className="text-[10px] text-[#666666] mb-2">{r.description}</p>
                <div className="flex items-center justify-between">
                  <span className={`px-1.5 py-0.5 rounded text-[9px] ${
                    r.type === "daily" ? "bg-[#D4EDDA] text-[#2D6A4F]" :
                    r.type === "weekly" ? "bg-[#E8F4FD] text-[#0369A1]" :
                    r.type === "monthly" ? "bg-[#FEF3C7] text-[#D97706]" :
                    "bg-[#FDF2B5] text-[#666666]"
                  }`}>
                    {r.type === "daily" ? "Ежедневно" : r.type === "weekly" ? "Еженедельно" : r.type === "monthly" ? "Ежемесячно" : "По запросу"}
                  </span>
                  {r.autoSchedule && (
                    <span className="px-1.5 py-0.5 rounded bg-[#D4EDDA] text-[#2D6A4F] text-[9px]">Авто</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Selected Report Actions */}
          {selectedReport && (
            <div className="mt-4 p-4 bg-[#FEF3C7] border border-[#D5CA8A] rounded-lg">
              <p className="text-[11px] font-semibold text-[#2D2D2D] mb-2">{selectedReport.name}</p>
              <p className="text-[10px] text-[#666666] mb-3">{selectedReport.description}</p>
              <div className="flex items-center gap-2">
                <button className="text-[10px] px-3 py-1.5 bg-[#E8DB6C] text-[#2D2D2D] rounded hover:bg-[#D4C55A] cursor-pointer transition-colors font-medium whitespace-nowrap">
                  <i className="ri-file-list-3-line mr-1"></i>
                  Сформировать
                </button>
                <button className="text-[10px] px-3 py-1.5 bg-[#FDF2B5] text-[#666666] rounded hover:bg-[#E8DB6C] cursor-pointer transition-colors whitespace-nowrap">
                  <i className="ri-settings-3-line mr-1"></i>
                  Настроить расписание
                </button>
                <button className="text-[10px] px-3 py-1.5 bg-[#FDF2B5] text-[#666666] rounded hover:bg-[#E8DB6C] cursor-pointer transition-colors whitespace-nowrap">
                  <i className="ri-download-line mr-1"></i>
                  Скачать PDF
                </button>
                <button className="text-[10px] px-3 py-1.5 bg-[#FDF2B5] text-[#666666] rounded hover:bg-[#E8DB6C] cursor-pointer transition-colors whitespace-nowrap">
                  <i className="ri-mail-send-line mr-1"></i>
                  Отправить
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#D5CA8A] bg-[#FFF9E6] rounded-b-lg">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-[11px] text-[#666666] hover:bg-[#FDF2B5] cursor-pointer transition-colors whitespace-nowrap">
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}